"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { Loader, CircleCheck, Calendar, User, Mail, Phone, Stethoscope, MessageSquare } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
const PROCEDURE_OPTIONS = ["CPAP Therapy", "No Current Treatment", "Other (Oral Appliance, etc.)"];
export default function InquireAboutSurgery() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="152944" id="get-your-custom-fit" className="relative py-24 overflow-hidden bg-background">
      {}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-primary/5 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute top-0 right-0 w-[400px] h-[400px] bg-secondary/10 rounded-full blur-3xl pointer-events-none translate-x-1/3 -translate-y-1/3" />

      <div className="container relative z-10 px-4 mx-auto max-w-3xl">
        <div ref={ref} className={`transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="text-center mb-10">
            <h2 className="text-3xl md:text-5xl font-bold text-primary mb-4 tracking-tight">
              Ready for a Better Fit?
            </h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              Experience the difference of a custom-designed oral appliance. Our clinical team is ready to help you achieve the deep, restorative sleep you deserve.
            </p>
          </div>

          <Card className="bg-card/80 backdrop-blur-xl border-border/50 shadow-2xl shadow-primary/10 relative overflow-hidden">
            {}
            <div className="absolute inset-0 bg-gradient-to-br from-secondary/5 to-transparent pointer-events-none" />
            
            <CardHeader className="pb-4 border-b border-border/30 relative z-10">
              <CardTitle className="text-2xl text-center text-primary">Start Your Journey to Better Sleep</CardTitle>
              <CardDescription className="text-center">
                All fields marked with an asterisk (*) are required.
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-8 relative z-10">
              <FormProvider formKey="custom_fit_form_oral_appliances" formId="custom_fit_form_oral_appliances" sectionName="inquire-about-surgery" defaultValues={{
              full_name: "",
              email: "",
              phone: "",
              procedure_interest: "",
              preferred_date: "",
              details: "",
              subscribe_newsletter: false
            }} validationRules={{
              full_name: {
                required: "Full Name is required"
              },
              email: {
                required: "Email Address is required",
                pattern: {
                  value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                  message: "Please enter a valid email address"
                }
              },
              phone: {
                required: "Phone Number is required",
                pattern: {
                  value: /^[\d\s\-\+\(\)]{10,20}$/,
                  message: "Please enter a valid phone number"
                }
              },
              procedure_interest: {
                required: "Please select your current treatment status"
              }
            }} submitText="Request Consultation" successMessage="Thank you for reaching out. Our sleep specialists will review your information and contact you shortly to discuss your custom-fit oral appliance.">
                <InquiryForm />
              </FormProvider>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>;
}
function InquiryForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center animate-in fade-in zoom-in duration-500">
        <div className="w-16 h-16 bg-secondary/10 rounded-full flex items-center justify-center mb-6">
          <CircleCheck className="w-8 h-8 text-secondary" />
        </div>
        <h3 className="text-2xl font-semibold text-primary mb-2">Inquiry Received</h3>
        <p className="text-muted-foreground max-w-md mb-8">
          {successMessage}
        </p>
        <Button onClick={resetForm} variant="outline" className="min-w-[200px] border-secondary text-secondary hover:bg-secondary/5">
          Submit Another Inquiry
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit}>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="full_name" render={({
        field
      }) => <FormItem className="md:col-span-2">
              <FormLabel className="flex items-center gap-2 font-medium">
                <User className="w-4 h-4 text-secondary" />
                Full Name *
              </FormLabel>
              <FormControl>
                <Input placeholder="Jane Doe" className="bg-background/50 border-input focus:ring-secondary" {...field} />
              </FormControl>
              <FormDescription>Enter your first and last name.</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" render={({
        field
      }) => <FormItem>
              <FormLabel className="flex items-center gap-2 font-medium">
                <Mail className="w-4 h-4 text-secondary" />
                Email Address *
              </FormLabel>
              <FormControl>
                <Input type="email" placeholder="jane@example.com" className="bg-background/50 border-input focus:ring-secondary" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone" render={({
        field
      }) => <FormItem>
              <FormLabel className="flex items-center gap-2 font-medium">
                <Phone className="w-4 h-4 text-secondary" />
                Phone Number *
              </FormLabel>
              <FormControl>
                <Input type="tel" placeholder="(555) 123-4567" className="bg-background/50 border-input focus:ring-secondary" {...field} />
              </FormControl>
              <FormDescription>We will use this number to contact you.</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="procedure_interest" render={({
        field
      }) => <FormItem className="md:col-span-2">
              <FormLabel className="flex items-center gap-2 font-medium">
                <Stethoscope className="w-4 h-4 text-secondary" />
                Current Treatment *
              </FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <FormControl>
                  <SelectTrigger className="bg-background/50 border-input focus:ring-secondary">
                    <SelectValue placeholder="Select your current treatment" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {PROCEDURE_OPTIONS.map((option, i) => <SelectItem key={i} value={option} data-index={i}>
                      {option}
                    </SelectItem>)}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="preferred_date" render={({
        field
      }) => <FormItem className="md:col-span-2">
              <FormLabel className="flex items-center gap-2 font-medium">
                <SafeHydrate><Calendar className="w-4 h-4 text-secondary" /></SafeHydrate>
                Preferred Consultation Date
              </FormLabel>
              <FormControl>
                <Input type="date" className="bg-background/50 border-input focus:ring-secondary" {...field} />
              </FormControl>
              <FormDescription>If you have a preferred date for a consultation, let us know.</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="details" render={({
        field
      }) => <FormItem className="md:col-span-2">
              <FormLabel className="flex items-center gap-2 font-medium">
                <MessageSquare className="w-4 h-4 text-secondary" />
                Sleep Symptoms or Concerns
              </FormLabel>
              <FormControl>
                <Textarea placeholder="Tell us about your snoring, daytime fatigue, or current CPAP challenges." className="min-h-[120px] bg-background/50 border-input focus:ring-secondary resize-y" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="subscribe_newsletter" render={({
        field
      }) => <FormItem className="md:col-span-2 flex flex-row items-start space-x-3 space-y-0 rounded-md border border-border/50 p-4 bg-background/30">
              <FormControl>
                <input type="checkbox" className="w-4 h-4 mt-0.5 rounded border-input bg-background text-secondary focus:ring-secondary focus:ring-offset-background accent-secondary cursor-pointer" checked={field.value} onChange={field.onChange} />
              </FormControl>
              <div className="space-y-1 leading-none">
                <FormLabel className="cursor-pointer font-medium">
                  Subscribe to our sleep-health newsletter
                </FormLabel>
                <FormDescription>
                  Receive updates on sleep care tips and clinic news.
                </FormDescription>
              </div>
            </FormItem>} />
      </div>

      {submitError && <div className="p-4 mt-6 bg-destructive/10 border border-destructive/20 rounded-lg text-destructive text-sm flex items-center gap-2">
          <span className="font-semibold">Error:</span> {submitError}
        </div>}

      <div className="mt-8 flex justify-end">
        <Button type="submit" size="lg" disabled={isSubmitting} className="w-full md:w-auto min-w-[200px] bg-secondary text-secondary-foreground hover:bg-secondary/90 shadow-lg shadow-secondary/20">
          {isSubmitting ? <>
              <Loader className="animate-spin mr-2 size-5" />
              Sending Inquiry...
            </> : 'Request Consultation'}
        </Button>
      </div>
    </Form>;
}